<!--
Author: Haziq Kamel
Purpose: This component is designed to showcase the Craftable project,
a Minecraft puzzle quiz game that was developed by a team of interns.
The component includes a title, feedback, and statistics about the game,
as well as an image and testimonial from one of the interns who worked on the project.

How to use this component:
1. Import the component into your desired Vue file:
  import CraftableShowcase from 'path/to/CraftableShowcase.vue';

2. Use the component in your template:
  <CraftableShowcase />
-->

<template>
  <div class="container">
    <div class="left-sub-container">
      <div class="title rounded-container">
        <h1>Craftable: Minecraft Puzzle Quiz Game</h1>
        <p>
          Craftable is another commercially successful product that began its life as an internship project. For two and a half months, a three-person team transformed an initial concept into a full-fledged game. From brainstorming and prototyping to design and development, the team demonstrated creativity and technical skills to bring Craftable to life, showcasing the power of teamwork to deliver a finished product within a short timeframe.
        </p>
      </div>
      <div class="feedback rounded-container">
        <div class="text-with-border">
          <p>I’m glad I took up the challenge and the experience gained is priceless!</p>
        </div>
        <div class="sub-feedback">
          <img
            src="https://res.cloudinary.com/alphapod/image/upload/v1526467701/alphapod_website/internjake.png"
            alt="internjake"
          >
          <div>
            <h3>Jake Tang</h3>
            <p>Former intern</p>
          </div>
        </div>
      </div>
      <div class="statistic rounded-container">
        <AnimatedNumberCount
          title="Downloads"
          :targeted-number="1100000"
          padding-right
        />
        <AnimatedNumberCount
          title="Playthroughs"
          :targeted-number="54000000"
          padding-in-between
        />
        <AnimatedNumberCount
          title="In-game taps"
          :targeted-number="1100000000"
          padding-left
        />
      </div>
    </div>
    <div class="right-sub-container rounded-container">
      <swiper
        id="ctable-swiper-left"
        :direction="'vertical'"
        :slides-per-view="'auto'"
        :space-between="0"
        :free-mode="true"
        :modules="[FreeMode, Autoplay]"
        :autoplay="{
          delay: 0,
          disableOnInteraction: false,
        }"
        :speed="5000"
        :loop="true"
        :lazy-preload-prev-next="3"
        :load-prev-next="true"
        :auto-height="false"
        :style="{ maxHeight: '1048px', overflow: 'hidden' }"
        @swiper="onSwiperLeft"
      >
        <swiper-slide
          v-for="(image, index) in imageLeftLoop"
          :key="index"
          :style="{
            maxHeight: isMobile ? '282px' : '519px',
            maxWidth: isMobile ? '143px' : '263px'
          }"
          :class="{
            'swiper-no-swiping': true,
            maxHeight: isMobile ? '282px' : '519px',
            maxWidth: isMobile ? '143px' : '263px'
          }"
        >
          <img
            :src="image"
            loading="lazy"
            :style="{
              maxHeight: isMobile ? '258px' : '100%',
              maxWidth: isMobile ? 'none' : '100%'
            }"
            alt="Craftable Image"
          >
          <div class="swiper-lazy-preloader" />
          <div class="swiper-lazy-preloader" />
        </swiper-slide>
      </swiper>
      <swiper
        id="ctable-swiper-right"
        :direction="'vertical'"
        :slides-per-view="'auto'"
        :space-between="0"
        :free-mode="true"
        :modules="[FreeMode, Autoplay]"
        :autoplay="{
          delay: 0,
          disableOnInteraction: false,
          reverseDirection: true,
        }"
        :speed="5000"
        :loop="true"
        :lazy-preload-prev-next="3"
        :load-prev-next="true"
        :auto-height="false"
        :style="{ maxHeight: '1048px', overflow: 'hidden' }"
        @swiper="onSwiperRight"
      >
        <swiper-slide
          v-for="(image, index) in imageRightLoop"
          :key="index"
          :style="{
            maxHeight: isMobile ? '282px' : '519px',
            maxWidth: isMobile ? '143px' : '263px'
          }"
          :class="{
            'swiper-no-swiping': true,
            maxHeight: isMobile ? '282px' : '519px',
            maxWidth: isMobile ? '143px' : '263px'
          }"
        >
          <img
            :src="image"
            alt="Craftable Image"
            loading="lazy"
            :style="{
              maxHeight: isMobile ? '258px' : '100%',
              maxWidth: isMobile ? 'none' : '100%'
            }"
          >
          <div class="swiper-lazy-preloader" />
          <div class="swiper-lazy-preloader" />
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { cdn } from '../../support/cdn';
import AnimatedNumberCount from './AnimatedNumberCount.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { FreeMode, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { Swiper as SwiperInstance } from 'swiper';

const isMobile = ref(window.innerWidth < 680);
const imageCountLeft = 3;
const imageCountRight = 3;
const imageLeft: string[] = [...Array(imageCountLeft).keys()].map((i) => cdn(`showcase/craftable/img_ap_internship_ctable${i + 1}.png`));
const imageRight: string[] = [...Array(imageCountRight).keys()].map((i) => cdn(`showcase/craftable/img_ap_internship_ctable${i + 4}.png`));

const imageLeftLoop = computed(() => [...Array(12).keys()].map(() => imageLeft).flat());
const imageRightLoop = computed(() => [...Array(12).keys()].map(() => imageRight).flat());

const swiperInstanceLeft = ref<SwiperInstance | null>(null);
const swiperInstanceRight = ref<SwiperInstance | null>(null);

function onSwiperLeft (swiper: SwiperInstance) {
  swiperInstanceLeft.value = swiper;
  if (swiperInstanceLeft.value) {
    setTimeout(() => {
      if (!swiperInstanceLeft.value?.autoplay?.running) {
        swiperInstanceLeft.value?.autoplay?.start();
      }
    }, 150);
  }
}

function onSwiperRight (swiper: SwiperInstance) {
  swiperInstanceRight.value = swiper;
  if (swiperInstanceRight.value) {
    setTimeout(() => {
      if (!swiperInstanceRight.value?.autoplay?.running) {
        swiperInstanceRight.value?.autoplay?.start();
      }
    }, 150);
  }
}

function onResize() {
  if (window.innerWidth < 680) {
    isMobile.value = true;
  } else {
    isMobile.value = false;
  }
}

onMounted(() => {
  onResize();
  window.addEventListener('resize', onResize);
  onResize();
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  window.removeEventListener('resize', onResize);
});
</script>

<style scoped>
#ctable-swiper .swiper-wrapper{
  transition-timing-function: linear !important;
}

span.highlight {
  color: #007AFF;
}

.rounded-container {
  background-color: #FAFAFA;
  border-radius: 16px;
  border: 1px solid #E6E6E6;
}

.statistic {
  display: flex;
  padding: 32px 40px;
  justify-content: space-around;
  background: white;
  overflow-x: auto;
  scrollbar-width: none;
  min-height: 140px;
}

.statistic::-webkit-scrollbar {
  display: none;
}

.feedback {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 64px 40px;
  background: white;
}

.sub-feedback {
  display: flex;
  gap: 24px;
  justify-content: flex-start;
  margin: 0 24px;
}

.sub-feedback h3 {
  font-size: 20px;
  font-weight: 700;
  line-height: 32px;
}

.sub-feedback p {
  font-size: 20px;
  line-height: 32px;
}

.text-with-border {
  display: flex;
  border-left: 2px solid #00CEB0;
}

.text-with-border p {
  font-size: 20px;
  line-height: 32px;
  padding-left: 24px;
}

.title {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 64px 40px;
  flex-grow: 1;
  background: white;
}

.title h1 {
  font-size: 48px;
  font-weight: 700;
  line-height: 56px;
  letter-spacing: -1px;
}

.title p {
  font-size: 20px;
  line-height: 32px;
}

.left-sub-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 628px;
  width: 100%;
}

.right-sub-container {
  display: flex;
  flex-direction: row;
  max-width: 628px;
  width: 100%;
  padding: 0 63px;
  background: white;
}

.container {
  display: flex;
  flex-direction: row-reverse;
  gap: 24px;
  max-width: 1280px;
  width: 100%;
  max-height: 1048px;
  max-height: 1048px;
  height: 100%;
  justify-content: center;
}

@media screen and (max-width: 768px) {
  .title {
    padding: 32px;
  }

  .title {
    padding: 32px;
  }

  .container {
    max-height: none;
  }

  .title h1 {
    font-size: 32px;
    line-height: 39px;
    letter-spacing: -0.75px;
  }

  .title p {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.25px;
  }

  .text-with-border p {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.25px;
  }

  .feedback {
    padding: 32px 16px;
  }

  .sub-feedback {
    gap: 16px;
  }

  .sub-feedback h3 {
    font-size: 14px;
    line-height: 20px;
  }

  .sub-feedback p {
    font-size: 14px;
    line-height: 20px;
  }

  .sub-feedback img {
    width: 40px;
    height: 40px;
  }

  .statistic {
    padding: 24px 32px;
    align-items: center;
    min-height: 0;
  }
}

@media screen and (max-width: 1024px) {
  .right-sub-container {
    max-height: 628px;
    max-height: 628px;
  }

  .container {
    flex-direction: column-reverse;
    align-items: center;
    max-height: none;
    max-height: none;
  }
}

@media screen and (max-width: 425px) {
  .right-sub-container {
    padding: 0 40px;
  }

  .statistic {
    justify-content: stretch;
  }

  .right-sub-container {
    max-height: 428px;
    padding: 0 40px;
  }

  .statistic {
    justify-content: stretch;
  }

  .right-sub-container {
    max-height: 428px;
  }
}

@media screen and (max-width: 375px) {
  .right-sub-container {
    padding: 0 28px;
    padding: 0 28px;
  }
}

@media screen and (max-width: 320px) {
  .right-sub-container{
    padding: 0 0;
    padding: 0 0;
  }
}

</style>
